import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { festivalsData } from '../../mocks/festivals-data';
import Navigation from '../../components/Navigation';

// Potli data for festivals that have potlis available
const festivalPotlisAvailable = [
  'diwali',
  'navaratri',
  'ganesh-chaturthi',
  'maha-shivaratri',
  'janmashtami',
  'pitru-paksha',
  'krishna-janmashtami',
  'holi',
  'ram-navami',
  'hanuman-jayanti',
  'karwa-chauth',
  'chhath-puja',
  'tulsi-vivah',
  'makar-sankranti',
  'vasant-panchami',
];

const potliPlans = [
  {
    id: 'essential',
    name: 'Essential Potli',
    price: '₹999 - ₹1,499',
    items: '12-15 items',
  },
  {
    id: 'complete',
    name: 'Complete Potli',
    price: '₹1,999 - ₹2,999',
    items: '20-25 items',
    badge: 'Most Chosen',
  },
  {
    id: 'premium',
    name: 'Premium Potli',
    price: '₹3,999 - ₹6,999',
    items: '25-30 items',
  },
];

// Explicit union type for the share platforms – helps TypeScript catch mistakes
type SharePlatform = 'whatsapp' | 'facebook' | 'twitter' | 'email' | 'copy';

export default function FestivalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [shareMessage, setShareMessage] = useState('');
  const [showLaunchModal, setShowLaunchModal] = useState(false);

  const festival = festivalsData.find((f) => f.id === id);
  const hasPotli = id ? festivalPotlisAvailable.includes(id) : false;

  // Redirect to list page if the festival does not exist
  useEffect(() => {
    if (!festival) {
      navigate('/festivals');
    }
  }, [festival, navigate]);

  // Reset scroll position when navigating between festivals
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const handleShare = async (platform: SharePlatform) => {
    if (!festival) return;

    const url = window.location.href;
    const text = `Check out ${festival.name} on ${festival.date} ${festival.month}! ${festival.description}`;

    let shareUrl = '';

    switch (platform) {
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          text,
        )}&url=${encodeURIComponent(url)}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${encodeURIComponent(
          festival.name,
        )}&body=${encodeURIComponent(`${text}\n\n${url}`)}`;
        break;
      case 'copy':
        try {
          await navigator.clipboard.writeText(url);
          setShareMessage('Link copied to clipboard!');
          setTimeout(() => setShareMessage(''), 3000);
        } catch (err) {
          console.error('Clipboard write failed', err);
          setShareMessage('Failed to copy link.');
          setTimeout(() => setShareMessage(''), 3000);
        }
        return; // No need to open a new window for copy action
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };

  // If festival data is still loading or invalid, render nothing (redirect handled above)
  if (!festival) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <Navigation />

      {/* Launch Modal */}
      {showLaunchModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-[#C78A2B]/10 flex items-center justify-center mx-auto mb-6">
              <i className="ri-rocket-line text-4xl text-[#C78A2B]"></i>
            </div>
            <h3 className="text-2xl font-semibold text-[#2E2E2E] mb-4">Platform is Not Launched Yet</h3>
            <p className="text-gray-600 mb-6 font-light">
              Stay tuned! Our platform will be launched soon. We're working hard to bring you authentic ritual services.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              For inquiries, please contact us at:<br />
              <span className="text-[#C78A2B] font-medium">info@ekdharma.com</span>
            </p>
            <button
              onClick={() => setShowLaunchModal(false)}
              className="w-full px-8 py-3 bg-[#C78A2B] text-white rounded-xl text-base font-medium hover:bg-[#B8860B] transition-all cursor-pointer"
            >
              Got It
            </button>
          </div>
        </div>
      )}

      {/* Share Message Toast */}
      {shareMessage && (
        <div className="fixed top-24 right-6 z-50 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in">
          <i className="ri-checkbox-circle-line text-xl"></i>
          <span>{shareMessage}</span>
        </div>
      )}

      {/* Hero Section with Festival Image */}
      <section className="pt-24 pb-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/festivals"
            className="inline-flex items-center gap-2 text-[#2E2E2E] hover:text-[#C78A2B] transition-colors mb-6 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            <span className="text-sm">Back to Festivals</span>
          </Link>

          <div
            className="rounded-2xl overflow-hidden shadow-xl relative"
            style={{ width: '100%', height: '400px' }}
          >
            <img
              src={festival.image}
              alt={festival.name}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>

            <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10">
              <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-full mb-3">
                    <i className="ri-calendar-event-line text-xs text-white"></i>
                    <span className="text-xs tracking-widest text-white uppercase">{festival.month}</span>
                  </div>
                  <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-white mb-2">{festival.name}</h1>
                  <p className="text-base sm:text-lg text-white/90 font-light max-w-2xl">{festival.description}</p>
                </div>
                <div className="bg-white rounded-xl px-6 py-4 shadow-xl text-center flex-shrink-0">
                  <div className="text-3xl sm:text-4xl font-bold text-[#C78A2B] leading-none">{festival.date}</div>
                  <div className="text-xs text-[#2E2E2E] mt-1">{festival.month}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Significance */}
              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-md">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#C78A2B]/10 flex items-center justify-center">
                    <i className="ri-star-line text-xl text-[#C78A2B]"></i>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-semibold text-[#2E2E2E]">Significance</h2>
                </div>
                <p className="text-base text-gray-700 leading-relaxed font-light">{festival.significance}</p>
              </div>

              {/* Festival Potli Section - NEW */}
              {hasPotli && (
                <div className="bg-gradient-to-br from-[#C78A2B]/10 to-[#C78A2B]/5 rounded-2xl p-6 sm:p-8 border border-[#C78A2B]/20">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#C78A2B] flex items-center justify-center">
                      <i className="ri-gift-line text-xl text-white"></i>
                    </div>
                    <h2 className="text-xl sm:text-2xl font-semibold text-[#2E2E2E]">{festival.name} Puja Potli</h2>
                  </div>
                  <p className="text-base text-gray-700 leading-relaxed font-light mb-6">
                    Get all the puja samagri you need for {festival.name} delivered to your doorstep. Choose from three potli options based on your
                    requirements.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                    {potliPlans.map((plan) => (
                      <div
                        key={plan.id}
                        className={`bg-white rounded-xl p-4 relative ${plan.badge ? 'border-2 border-[#C78A2B]' : ''}`}
                      >
                        {plan.badge && (
                          <div className="absolute -top-2.5 left-1/2 transform -translate-x-1/2">
                            <span className="bg-[#C78A2B] text-white px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap">
                              {plan.badge}
                            </span>
                          </div>
                        )}
                        <h3 className="text-sm font-semibold text-[#2E2E2E] mb-1">{plan.name}</h3>
                        <p className="text-lg font-bold text-[#C78A2B] mb-1">{plan.price}</p>
                        <p className="text-xs text-gray-500">{plan.items}</p>
                      </div>
                    ))}
                  </div>

                  <Link
                    to={`/festival-potli/${id}`}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#C78A2B] text-white rounded-lg text-sm font-medium hover:bg-[#B8860B] transition-all cursor-pointer"
                  >
                    <i className="ri-gift-line"></i>
                    View Potli Details &amp; Order
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              )}

              {/* Detailed Story */}
              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-md">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#C78A2B]/10 flex items-center justify-center">
                    <i className="ri-book-open-line text-xl text-[#C78A2B]"></i>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-semibold text-[#2E2E2E]">The Story Behind</h2>
                </div>
                <p className="text-base text-gray-700 leading-relaxed font-light">{festival.detailedStory}</p>
              </div>

              {/* Traditional Rituals */}
              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-md">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#C78A2B]/10 flex items-center justify-center">
                    <i className="ri-ancient-gate-line text-xl text-[#C78A2B]"></i>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-semibold text-[#2E2E2E]">Traditional Rituals</h2>
                </div>
                <div className="space-y-4">
                  {festival.detailedRituals.map((ritual, index) => (
                    <div key={index} className="flex gap-3">
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#C78A2B] text-white flex items-center justify-center font-semibold text-xs">
                        {index + 1}
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-[#2E2E2E] mb-1">{ritual.title}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed font-light">{ritual.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Spiritual Benefits */}
              <div className="bg-[#FAF7F2] rounded-2xl p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                    <i className="ri-heart-line text-xl text-[#C78A2B]"></i>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-semibold text-[#2E2E2E]">Spiritual Benefits</h2>
                </div>
                <ul className="space-y-3">
                  {festival.spiritualBenefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-lg text-[#C78A2B] mt-0.5"></i>
                      <span className="text-base text-gray-700 font-light">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Puja Items Required */}
              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-md">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#C78A2B]/10 flex items-center justify-center">
                    <i className="ri-shopping-basket-line text-xl text-[#C78A2B]"></i>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-semibold text-[#2E2E2E]">Puja Items Required</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {festival.pujaItems.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 bg-[#FAF7F2] rounded-lg">
                      <i className="ri-checkbox-circle-line text-lg text-[#C78A2B]"></i>
                      <span className="text-sm text-[#2E2E2E]">{item}</span>
                    </div>
                  ))}
                </div>

                {hasPotli && (
                  <div className="mt-6 p-4 bg-[#C78A2B]/10 rounded-lg">
                    <p className="text-sm text-gray-700 mb-3">
                      <i className="ri-lightbulb-line text-[#C78A2B] mr-2"></i>
                      Don't want to gather items separately? Get our ready‑made potli with all items included.
                    </p>
                    <Link
                      to={`/festival-potli/${id}`}
                      className="text-sm text-[#C78A2B] font-medium hover:underline cursor-pointer"
                    >
                      Order {festival.name} Potli →
                    </Link>
                  </div>
                )}
              </div>

              {/* Mantras */}
              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-md">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#C78A2B]/10 flex items-center justify-center">
                    <i className="ri-music-2-line text-xl text-[#C78A2B]"></i>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-semibold text-[#2E2E2E]">Sacred Mantras</h2>
                </div>
                <div className="space-y-4">
                  {festival.mantras.map((mantra, index) => (
                    <div key={index} className="p-4 bg-[#FAF7F2] rounded-xl border-l-4 border-[#C78A2B]">
                      <h3 className="text-sm font-semibold text-[#2E2E2E] mb-2">{mantra.title}</h3>
                      <p className="text-base font-light text-[#C78A2B] italic mb-2">{mantra.sanskrit}</p>
                      <p className="text-sm text-gray-600 font-light">{mantra.meaning}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-28 space-y-6">
                {/* Book Ritual Card */}
                <div className="bg-[#C78A2B] rounded-2xl p-6 text-white shadow-xl">
                  <h3 className="text-xl font-semibold mb-3">Book This Ritual</h3>
                  <p className="text-sm mb-4 text-white/90 font-light">
                    Experience authentic {festival.name} puja performed by experienced priests.
                  </p>
                  <button
                    onClick={() => setShowLaunchModal(true)}
                    className="w-full px-5 py-2.5 bg-white text-[#C78A2B] rounded-lg text-sm font-semibold hover:bg-gray-100 transition-all inline-flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    Book Now
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </div>

                {/* Quick Info */}
                <div className="bg-white rounded-2xl p-6 shadow-md">
                  <h3 className="text-lg font-semibold text-[#2E2E2E] mb-4">Quick Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <i className="ri-calendar-line text-lg text-[#C78A2B] mt-0.5"></i>
                      <div>
                        <div className="text-xs text-gray-500">Date</div>
                        <div className="text-sm font-medium text-[#2E2E2E]">
                          {festival.date} {festival.month}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-time-line text-lg text-[#C78A2B] mt-0.5"></i>
                      <div>
                        <div className="text-xs text-gray-500">Best Time</div>
                        <div className="text-sm font-medium text-[#2E2E2E]">{festival.bestTime}</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-map-pin-line text-lg text-[#C78A2B] mt-0.5"></i>
                      <div>
                        <div className="text-xs text-gray-500">Celebrated</div>
                        <div className="text-sm font-medium text-[#2E2E2E]">{festival.celebratedIn}</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-user-star-line text-lg text-[#C78A2B] mt-0.5"></i>
                      <div>
                        <div className="text-xs text-gray-500">Deity</div>
                        <div className="text-sm font-medium text-[#2E2E2E]">{festival.deity}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Share Card */}
                <div className="bg-white rounded-2xl p-6 shadow-md">
                  <h3 className="text-lg font-semibold text-[#2E2E2E] mb-3">Share</h3>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleShare('whatsapp')}
                      className="flex-1 px-3 py-2 bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-lg text-xs font-medium transition-colors cursor-pointer flex items-center justify-center gap-1 whitespace-nowrap"
                    >
                      <i className="ri-whatsapp-line"></i>
                      WhatsApp
                    </button>
                    <button
                      onClick={() => handleShare('facebook')}
                      className="flex-1 px-3 py-2 bg-[#1877F2] hover:bg-[#0C63D4] text-white rounded-lg text-xs font-medium transition-colors cursor-pointer flex items-center justify-center gap-1 whitespace-nowrap"
                    >
                      <i className="ri-facebook-fill"></i>
                      Facebook
                    </button>
                    <button
                      onClick={() => handleShare('copy')}
                      className="w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 text-[#2E2E2E] rounded-lg text-xs font-medium transition-colors cursor-pointer flex items-center justify-center gap-1 whitespace-nowrap"
                    >
                      <i className="ri-link"></i>
                      Copy Link
                    </button>
                  </div>
                </div>

                {/* Related Festivals */}
                <div className="bg-white rounded-2xl p-6 shadow-md">
                  <h3 className="text-lg font-semibold text-[#2E2E2E] mb-4">Related Festivals</h3>
                  <div className="space-y-3">
                    {festivalsData
                      .filter((f) => f.id !== festival.id && f.category === festival.category)
                      .slice(0, 3)
                      .map((relatedFestival) => (
                        <Link
                          key={relatedFestival.id}
                          to={`/festival/${relatedFestival.id}`}
                          className="block p-3 bg-[#FAF7F2] rounded-lg hover:bg-[#C78A2B]/10 transition-all cursor-pointer"
                        >
                          <div className="text-sm font-semibold text-[#2E2E2E]">{relatedFestival.name}</div>
                          <div className="text-xs text-gray-600">
                            {relatedFestival.date} {relatedFestival.month}
                          </div>
                        </Link>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black rounded-t-[60px] pt-16 pb-10 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <img
              src="https://public.readdy.ai/ai/img_res/15313995-b9c8-4137-afe3-659f58e4dd9d.png"
              alt="EkDharma Logo"
              className="h-10 w-10 object-contain"
            />
            <h3 className="text-3xl font-light text-white">EkDharma</h3>
          </div>
          <p className="text-gray-400 text-sm mb-8">
            © {new Date().getFullYear()} EkDharma. All rights reserved. ·{' '}
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#C78A2B] transition-colors"
            >
              Web Design
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
